import React, { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import { storeData, updateData } from '~/store/modules/estoqueAreia/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.estoqueAreia);

  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  const [silo, setSilo] = useState(initials?.silotipoareia || 'N');

  const isEdicao = useMemo(() => !create, [create]);

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),

        idtipoareia: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),

        nomepostotipoareia:
          silo === 'S'
            ? Yup.string()
                .trim()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
                .test(
                  'characters-invalid',
                  t('s.configuracao.geral.naopodecontercaracterespecial'),
                  (value) => validCharacters(value)
                )
            : Yup.string().nullable(),

        capacidadepostotipoareia:
          silo === 'S'
            ? Yup.number()
                .transform((value, originalValue) =>
                  originalValue === '' || originalValue === null
                    ? undefined
                    : value
                )
                .typeError(t('s.geral.validacao.campoobrigatorio'))
                .integer(t('s.geral.validacao.campoinvalido'))
                .required(t('s.geral.validacao.campoobrigatorio'))
                .moreThan(
                  0,
                  t('s.geral.validacao.valorminimo').replace('%s', 1)
                )
                .lessThan(
                  50001,
                  t('s.geral.validacao.valormaximo').replace('%s', 50000)
                )
            : Yup.number().nullable(),
      });

      await schema.validate(param, { abortEarly: false });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,

        idposto: create ? Number(param.idposto) : initials.idposto,
        idtipoareia: create ? Number(param.idtipoareia) : initials.idtipoareia,
        nomepostotipoareia:
          silo === 'S' ? param.nomepostotipoareia?.trim() : null,

        capacidadepostotipoareia:
          silo === 'S' ? Number(param.capacidadepostotipoareia) : null,
      };

      console.log(objSend);

      if (create) {
        dispatch(storeData(objSend, params));
      } else {
        dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
        return;
      }
      console.error(err);
    }
  }

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);

      if (!create) {
        setSilo(initials?.silotipoareia || 'N');
      }
    }
  }, [loading, initials, reference, create]);

  return loading ? (
    <div style={{ height: '14rem', width: '50rem' }}>
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <Autocomplete
        name="idposto"
        label="codigonomeposto"
        url="postos"
        unique="idposto"
        title={t('s.geral.postocomboio')}
        disabled={!create}
        getValue={(value) => {
          if (value) {
            reference.current.setFieldValue('idposto', value.idposto);
          } else {
            reference.current.setFieldValue('idposto', null);
          }
        }}
      />

      <Autocomplete
        name="idtipoareia"
        label="descricaotipoareia"
        url="tiposareia"
        unique="idtipoareia"
        title={t('s.cadastro.pitstop.tipoareia')}
        disabled={!create}
        getValue={(value) => {
          if (value) {
            reference.current.setFieldValue('idtipoareia', value.idtipoareia);

            const tipoSilo = value.silotipoareia || 'N';

            setSilo(tipoSilo);

            if (tipoSilo !== 'S') {
              reference.current.setFieldValue('nomepostotipoareia', null);
              reference.current.setFieldValue('capacidadepostotipoareia', null);
            }
          } else {
            setSilo('N');
            reference.current.setFieldValue('idtipoareia', null);
            reference.current.setFieldValue('nomepostotipoareia', null);
            reference.current.setFieldValue('capacidadepostotipoareia', null);
          }
        }}
      />

      {silo === 'S' && (
        <>
          <Input
            name="nomepostotipoareia"
            label={t('s.configuracao.posto.nomeposto')}
            variant="outlined"
            fullWidth
            size="small"
            inputProps={{ maxLength: 50 }}
            InputLabelProps={{ shrink: true }}
          />

          <Input
            name="capacidadepostotipoareia"
            label={t('s.relatorio.afericao.volume')}
            variant="outlined"
            fullWidth
            size="small"
            type="number"
            InputLabelProps={{ shrink: true }}
          />
        </>
      )}
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
